import { Heading, Pilcrow, List, ListOrdered, Image, Images, Code2, StickyNote } from "lucide-react";

// Block type registry + defaults for the article content builder.

export const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: Heading, description: "Section title", color: "primary" },
  { type: "paragraph", label: "Paragraph", icon: Pilcrow, description: "Body text", color: "secondary" },
  { type: "bulletList", label: "Bullet List", icon: List, description: "Unordered list", color: "info" },
  { type: "numberedList", label: "Numbered List", icon: ListOrdered, description: "Ordered list", color: "info" },
  { type: "image", label: "Image", icon: Image, description: "Picture with caption", color: "warning" },
  { type: "gallery", label: "Image Gallery", icon: Images, description: "Multiple photos in a collage", color: "warning" },
  { type: "code", label: "Code Block", icon: Code2, description: "Formatted code snippet", color: "neutral" },
  { type: "note", label: "Note", icon: StickyNote, description: "Callout / tip", color: "success" },
];

// Semantic-token accent classes per block type — keyed off the theme's
// existing color tokens so nothing here is a hardcoded color.
export const BLOCK_COLOR_CLASSES = {
  primary: { bg: "bg-primary_tint", text: "text-primary", border: "border-primary" },
  secondary: { bg: "bg-secondary_tint", text: "text-secondary", border: "border-secondary" },
  info: { bg: "bg-info_bg", text: "text-info", border: "border-info_border" },
  warning: { bg: "bg-warning_bg", text: "text-warning", border: "border-warning_border" },
  danger: { bg: "bg-danger_bg", text: "text-danger", border: "border-danger_border" },
  success: { bg: "bg-success_bg", text: "text-success", border: "border-success_border" },
  neutral: { bg: "bg-surface_alt", text: "text-text_secondary", border: "border-border_strong" },
};

export const getBlockType = (type) => BLOCK_TYPES.find((b) => b.type === type) || BLOCK_TYPES[1];
export const getBlockColors = (type) => BLOCK_COLOR_CLASSES[getBlockType(type).color] || BLOCK_COLOR_CLASSES.neutral;

// KB_STATUS (tbl_knowledge_article.status) — shared between the article
// builder's publish step and the article list's status column.
export const KB_STATUS_META = {
  DRAFT: { label: "Draft", classes: "bg-surface_alt text-text_secondary border-border_strong" },
  SCHEDULED: { label: "Scheduled", classes: "bg-info_bg text-info border-info_border" },
  PUBLISHED: { label: "Published", classes: "bg-success_bg text-success border-success_border" },
};
export const getStatusMeta = (status) => KB_STATUS_META[status] || KB_STATUS_META.DRAFT;

export const NOTE_TYPES = [
  { value: "info", label: "Info" },
  { value: "tip", label: "Tip" },
  { value: "warning", label: "Warning" },
  { value: "success", label: "Success" },
];

export const CODE_LANGUAGES = ["javascript", "typescript", "python", "json", "bash", "html", "css", "sql"];

export const IMAGE_SIZES = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "full", label: "Full width" },
];

export const ALIGN_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];

export const GALLERY_COLUMNS = [
  { value: 2, label: "2 Columns" },
  { value: 3, label: "3 Columns" },
  { value: 4, label: "4 Columns" },
];

const DEFAULT_DATA = {
  heading: { text: "New heading", level: 2, align: "left" },
  paragraph: { text: "Write something..." },
  bulletList: { items: ["First point", "Second point"] },
  numberedList: { items: ["First step", "Second step"] },
  image: { src: "", alt: "", caption: "", size: "medium", align: "center" },
  gallery: {
    columns: 2,
    images: [
      { src: "", alt: "", caption: "" },
      { src: "", alt: "", caption: "" },
    ],
  },
  code: { language: "javascript", code: "console.log('hello world');", showLineNumbers: false },
  note: { noteType: "info", title: "", text: "Something worth calling out." },
};

let uidCounter = 0;
export const makeBlockId = () => `blk-${Date.now()}-${uidCounter++}`;

export const createBlock = (type) => ({
  id: makeBlockId(),
  type,
  data: { ...DEFAULT_DATA[type] },
});

// Frontend block `type` <-> backend `KB_CONTENT_TYPE` enum. One block type
// omitted here on purpose: the schema's PDF content-type has no frontend
// block — file attachments are handled entirely by the Attachments step
// (tbl_kb_attachment), not as an inline content block.
export const BLOCK_TYPE_TO_BACKEND = {
  heading: "HEADING",
  paragraph: "PARAGRAPH",
  bulletList: "BULLET_LIST",
  numberedList: "NUMBERED_LIST",
  image: "IMAGE",
  gallery: "GALLERY",
  code: "CODE_BLOCK",
  note: "NOTE",
};

export const BACKEND_TYPE_TO_BLOCK = Object.fromEntries(
  Object.entries(BLOCK_TYPE_TO_BACKEND).map(([blockType, backendType]) => [backendType, blockType])
);

// blocks (as edited in the builder) -> the `contents` array the API expects
export const blocksToContents = (blocks) =>
  blocks
    .map((b) => ({ type: BLOCK_TYPE_TO_BACKEND[b.type], content: b.data }))
    .filter((c) => !!c.type);

// the API's `kb_contents` (ordered by display_order) -> builder blocks
export const contentsToBlocks = (contents) =>
  (contents || [])
    .slice()
    .sort((a, b) => a.display_order - b.display_order)
    .map((c) => ({
      id: makeBlockId(),
      type: BACKEND_TYPE_TO_BLOCK[c.type] || "paragraph",
      data: c.content || {},
    }));
